// maps — where someone is, and how to show them somewhere.
//
// Deliberately NOT an embedded map. Every tile provider worth using needs an
// account, a key and a bill, and baking one in would make a scaffold decision
// on someone's behalf that costs them money. What is here works everywhere with
// no key: read a position, and hand a place to the map app the person already
// has, which is also the one that has their preferences and their car.
//
// If the app genuinely needs tiles in the page, add a provider — this addon is
// the location half, and it stays useful either way.

import { plugin, isNative } from '../../src/core/native.js';
import { toast } from '../../src/core/util.js';

/** @typedef {{ lat: number, lng: number, accuracy: number|null }} Position */

/**
 * Where the person is now.
 *
 * Returns null rather than throwing when permission is refused: a refusal is an
 * answer, and a feature that explodes because someone said no is a bug.
 *
 * @param {{ highAccuracy?: boolean, timeoutMs?: number }} [options]
 * @returns {Promise<Position|null>}
 */
export async function currentPosition({ highAccuracy = false, timeoutMs = 10_000 } = {}) {
  const Geolocation = isNative ? plugin('Geolocation') : null;
  try {
    if (Geolocation?.getCurrentPosition) {
      const p = await Geolocation.getCurrentPosition({
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
      });
      return {
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        accuracy: p.coords.accuracy ?? null,
      };
    }
    if (!navigator.geolocation) return null;
    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy ?? null,
        }),
        () => resolve(null),
        { enableHighAccuracy: highAccuracy, timeout: timeoutMs },
      );
    });
  } catch {
    return null;
  }
}

/**
 * Open a place in whichever map app this device prefers.
 *
 * iOS gets the `maps://` scheme so Apple Maps opens directly instead of
 * bouncing through a browser; everything else gets a Google Maps URL, which
 * Android hands to the installed app.
 *
 * @param {{ lat?: number, lng?: number, query?: string, label?: string }} place
 */
export function openInMaps({ lat, lng, query, label } = {}) {
  const name = encodeURIComponent(label ?? query ?? 'Here');
  const isApple = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent) && !window.MSStream;

  let url;
  if (typeof lat === 'number' && typeof lng === 'number') {
    url = isApple
      ? `maps://?q=${name}&ll=${lat},${lng}`
      : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  } else if (query) {
    const q = encodeURIComponent(query);
    url = isApple
      ? `maps://?q=${q}`
      : `https://www.google.com/maps/search/?api=1&query=${q}`;
  } else {
    return false;
  }

  window.open(url, isApple ? '_self' : '_blank', 'noopener');
  return true;
}

/** Metres between two points. Enough for "is this nearby", not for navigation. */
export function distanceMetres(a, b) {
  const R = 6_371_000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

/** Ask once, at the moment the feature is used, and say so if it is refused. */
export async function requestPosition() {
  const position = await currentPosition();
  if (!position) toast('Location is off for this app');
  return position;
}

export default {
  id: 'maps',
  // No boot, and no permission prompt on launch: a location prompt before the
  // person has asked for anything is the single most reliable way to be denied.
};
